/**
 * Control de arqueos de caja por sucursal.
 *
 * El caso: un dueño con varias sucursales revisa los arqueos de cierre y
 * necesita marcar cuáles ya controló. Hasta ahora el "Historial interno" de
 * Caja traía los últimos 50 turnos cerrados de TODA la organización, sin filtro
 * por sucursal y sin ningún campo de revisión: el control vivía en un cuaderno.
 *
 * Piezas:
 *  - `organizations.enable_arqueo_review`: el lógico. Apagado (default) nada
 *    cambia, ninguna organización existente ve la función aparecer.
 *  - `organizations.arqueo_review_since`: fecha de corte, escrita la PRIMERA vez
 *    que se activa. Sin ella el contador de pendientes arrancaría con todo el
 *    histórico (861 turnos cerrados en la base de Impulza al implementarlo).
 *  - `turnos_caja.controlado_at`: única fuente de verdad del estado. No hay
 *    columna `control_estado` a propósito: un estado persistido junto a la fecha
 *    permite la fila "controlada" sin fecha.
 *  - `turnos_caja.control_historial`: append de cada marcar/deshacer, para que
 *    deshacer no borre el rastro. No existe tabla de auditoría genérica en el
 *    esquema que se pudiera reutilizar.
 *
 * Quién controla: solo admin/dueño de la empresa. El chequeo está acá para la UI
 * y ADEMÁS en la base (trigger `tr_turnos_caja_guard_control`), porque la RLS de
 * `turnos_caja` da UPDATE a cualquier miembro de la organización: sin el trigger
 * un cajero marca su propio arqueo con un fetch directo a la API.
 */

import { buildLocalDateRangeTimestamp, formatLocalTimestampWithOffset } from './localDateTime.js';

/**
 * Roles que pueden controlar. Se normaliza con lower() porque en la base
 * conviven `admin` y `administrador` (y `superadmin` en Gastify): comparar
 * contra 'admin' suelto deja afuera a dueños reales. Es la misma lista que
 * valida el trigger.
 */
export const ARQUEO_REVIEW_ROLES = new Set(['admin', 'administrador', 'superadmin']);

/** Tope del historial embebido: el jsonb vive en la fila del turno. */
export const CONTROL_HISTORIAL_MAX = 50;

export const CONTROL_ESTADO = {
  PENDIENTE: 'pendiente',
  CONTROLADO: 'controlado',
};

const normalizeRole = (profile) => String(profile?.role || '').trim().toLowerCase();

/** @returns {boolean} organizations.enable_arqueo_review */
export function isArqueoReviewEnabled(organization) {
  return organization?.enable_arqueo_review === true;
}

/**
 * ¿Este usuario ve y usa el control de arqueos?
 *
 * Requiere el lógico activo: sin él la pestaña no existe para nadie, ni para el
 * soporte de la plataforma. Es una función de la empresa, no un permiso global.
 */
export function canReviewArqueos({ organization = null, profile = null, isSuperAdmin = false } = {}) {
  if (!isArqueoReviewEnabled(organization)) return false;
  if (isSuperAdmin) return true;
  if (!profile) return false;
  return ARQUEO_REVIEW_ROLES.has(normalizeRole(profile));
}

/** Estado derivado de `controlado_at`. Nunca se persiste. */
export function resolveControlEstado(turno) {
  return turno?.controlado_at ? CONTROL_ESTADO.CONTROLADO : CONTROL_ESTADO.PENDIENTE;
}

export function isArqueoControlado(turno) {
  return resolveControlEstado(turno) === CONTROL_ESTADO.CONTROLADO;
}

/**
 * ¿El turno entra al control?
 *
 * Solo los cerrados a partir de `arqueo_review_since`. Los anteriores se pueden
 * mirar con el filtro "Todos", pero no son pendientes: al activar el lógico en
 * una empresa con años de historia el contador tiene que arrancar en cero.
 *
 * `fecha_cierre` es `timestamptz` (un instante), así que se compara como
 * instante contra el corte, que también lo es.
 */
export function entraAlCorte(turno, organization) {
  if (turno?.estado !== 'Cerrado') return false;
  const corte = organization?.arqueo_review_since;
  if (!corte) return false;
  if (!turno?.fecha_cierre) return false;

  const cierre = new Date(turno.fecha_cierre).getTime();
  const desde = new Date(corte).getTime();
  if (!Number.isFinite(cierre) || !Number.isFinite(desde)) return false;
  return cierre >= desde;
}

/** Marcable: turno cerrado, dentro del corte y todavía pendiente. */
export function isArqueoControlable({ turno = null, organization = null } = {}) {
  if (!entraAlCorte(turno, organization)) return false;
  return !isArqueoControlado(turno);
}

/**
 * El cajero del turno y el que controla son la misma persona. Pasa de verdad en
 * la sucursal donde atiende el dueño: se permite, pero la fila lo muestra para
 * que quede visible en una auditoría.
 */
export function esAutocontrol(turno) {
  const cajero = String(turno?.usuario_email || '').trim().toLowerCase();
  const controlador = String(turno?.controlado_por_email || '').trim().toLowerCase();
  if (!cajero || !controlador) return false;
  return cajero === controlador;
}

/**
 * Agrega un evento al historial embebido, tolerando un valor sucio (null, texto
 * de una fila vieja, objeto). Recorta por el final: los eventos recientes son
 * los que importan.
 */
export function appendHistorial(historialActual, evento) {
  const base = Array.isArray(historialActual) ? historialActual : [];
  const next = [...base, evento];
  return next.length > CONTROL_HISTORIAL_MAX ? next.slice(next.length - CONTROL_HISTORIAL_MAX) : next;
}

const buildEvento = (accion, profile, nota) => ({
  accion,
  user_id: profile?.id || null,
  email: profile?.email || null,
  at: formatLocalTimestampWithOffset(new Date()),
  ...(nota ? { obs: String(nota).trim() } : {}),
});

/**
 * Payload del marcado. `controlado_por` va con `profile.id`, que ES el auth.uid
 * (AuthContext lee profiles con `.eq('id', userId)`): el trigger rechaza una
 * firma que no sea la del usuario de la sesión.
 */
export function buildMarcarControlPayload({ profile = null, turno = null, observacion = '' } = {}) {
  const nota = String(observacion || '').trim();
  return {
    controlado_at: formatLocalTimestampWithOffset(new Date()),
    controlado_por: profile?.id || null,
    controlado_por_email: profile?.email || null,
    control_observacion: nota || null,
    control_historial: appendHistorial(turno?.control_historial, buildEvento('controlado', profile, nota)),
  };
}

/**
 * Payload del deshacer: vuelve a pendiente pero NO borra el historial, que es lo
 * único que deja rastro de que alguien había aprobado ese arqueo.
 */
export function buildDeshacerControlPayload({ profile = null, turno = null, motivo = '' } = {}) {
  const nota = String(motivo || '').trim();
  return {
    controlado_at: null,
    controlado_por: null,
    controlado_por_email: null,
    control_observacion: null,
    control_historial: appendHistorial(turno?.control_historial, buildEvento('deshecho', profile, nota)),
  };
}

/**
 * Rango de un filtro de fechas sobre `fecha_cierre` (`timestamptz`).
 *
 * Fin de día inclusivo: el `lte` con 23:59:59.999 local incluye el turno cerrado
 * a las 23:50. Nunca armar estos bordes a mano con `T00:00:00.000Z`.
 */
export function buildRangoControl({ desde = '', hasta = '' } = {}) {
  return {
    gte: buildLocalDateRangeTimestamp(desde) || null,
    lte: buildLocalDateRangeTimestamp(hasta, true) || null,
  };
}

/** Filtro en memoria, espejo del filtro que se manda a la consulta. */
export function filtrarArqueos(turnos, { estado = 'pendiente', sitioId = '', organization = null } = {}) {
  const lista = Array.isArray(turnos) ? turnos : [];
  return lista.filter((turno) => {
    if (turno?.estado !== 'Cerrado') return false;
    if (sitioId && (turno?.sitio_id || '') !== sitioId) return false;
    if (estado === 'todos') return true;
    if (!entraAlCorte(turno, organization)) return false;
    return estado === CONTROL_ESTADO.CONTROLADO ? isArqueoControlado(turno) : !isArqueoControlado(turno);
  });
}

/** Datos de la firma para la fila y el detalle. */
export function resumenControl(turno) {
  return {
    estado: resolveControlEstado(turno),
    email: turno?.controlado_por_email || null,
    at: turno?.controlado_at || null,
    observacion: turno?.control_observacion || null,
    autocontrol: esAutocontrol(turno),
    eventos: Array.isArray(turno?.control_historial) ? turno.control_historial.length : 0,
  };
}
